/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { frameworkDomain } from '@app/app/shared/helpers/evidence-block.helper';
import { EvidenceMediaType, FrameworkEvidenceDomain } from '@app/app/shared/models/editor/form-content.model';

/**
 * An evidence block's settings as the author edits them, every kind in one shape: `EVIDENCE` reads the item limits and
 * its optional tag, `OPEN_EVIDENCE` the group limits, `FRAMEWORK_EVIDENCE` the framework and its domains. Mirrors the
 * node's attributes.
 */
export interface FormEvidenceConfig {
  kind: 'EVIDENCE' | 'OPEN_EVIDENCE' | 'FRAMEWORK_EVIDENCE';
  uid: string;
  title: string;
  required: boolean;
  allowedMediaTypes: EvidenceMediaType[];
  requireDescription: boolean;
  /** `EVIDENCE` */
  minItems: number;
  maxItems: number | null;
  tagId: string | null;
  /** `EVIDENCE` (with `tagId`) and `FRAMEWORK_EVIDENCE` */
  frameworkUid: string | null;
  /** `OPEN_EVIDENCE` */
  minGroups: number;
  minItemsPerGroup: number;
  maxGroups: number | null;
  /** `FRAMEWORK_EVIDENCE` */
  domains: FrameworkEvidenceDomain[];
  allowLearnerDomains: boolean;
}

/** The tiptap node each kind of block is. */
export const EVIDENCE_NODE_TYPES: Readonly<Record<FormEvidenceConfig['kind'], string>> = {
  EVIDENCE: 'formEvidence',
  OPEN_EVIDENCE: 'formOpenEvidence',
  FRAMEWORK_EVIDENCE: 'formFrameworkEvidence',
};

/** Where a kind's copy lives: `translations.v3Forms.evidence.<key>`. */
export function evidenceKindKey(kind: FormEvidenceConfig['kind']): 'slot' | 'open' | 'framework' {
  return kind === 'OPEN_EVIDENCE' ? 'open' : kind === 'FRAMEWORK_EVIDENCE' ? 'framework' : 'slot';
}

/** The kind of block a tiptap node is. */
export function evidenceKindOfNode(nodeType: string): FormEvidenceConfig['kind'] {
  const kinds = Object.keys(EVIDENCE_NODE_TYPES) as FormEvidenceConfig['kind'][];
  return kinds.find((kind) => EVIDENCE_NODE_TYPES[kind] === nodeType) ?? 'EVIDENCE';
}

/** The node attributes a config writes back. `kind` is the node type, so it is never written. */
export function formEvidenceAttributes(config: FormEvidenceConfig): Record<string, unknown> {
  const shared = {
    uid: config.uid,
    title: config.title,
    required: config.required,
    allowedMediaTypes: [...config.allowedMediaTypes],
    requireDescription: config.requireDescription,
  };
  switch (config.kind) {
    case 'EVIDENCE':
      return { ...shared, minItems: config.minItems, maxItems: config.maxItems, frameworkUid: config.frameworkUid, tagId: config.tagId };
    case 'OPEN_EVIDENCE':
      return { ...shared, minGroups: config.minGroups, minItemsPerGroup: config.minItemsPerGroup, maxGroups: config.maxGroups };
    case 'FRAMEWORK_EVIDENCE':
      return {
        ...shared,
        frameworkUid: config.frameworkUid,
        domains: config.domains.map((domain) => ({ ...domain, allowedMediaTypes: [...domain.allowedMediaTypes] })),
        allowLearnerDomains: config.allowLearnerDomains,
      };
  }
}

/** Reads a node's attributes, whatever they hold, into a complete config. */
export function formEvidenceConfigFrom(kind: FormEvidenceConfig['kind'], attrs: Record<string, unknown>): FormEvidenceConfig {
  return {
    kind,
    uid: (attrs['uid'] as string | null) ?? '',
    title: (attrs['title'] as string | null) ?? '',
    required: attrs['required'] !== false && attrs['required'] !== 'false',
    allowedMediaTypes: Array.isArray(attrs['allowedMediaTypes']) ? (attrs['allowedMediaTypes'] as EvidenceMediaType[]) : [],
    requireDescription: attrs['requireDescription'] === true || attrs['requireDescription'] === 'true',
    minItems: toCount(attrs['minItems'], 1),
    maxItems: toLimit(attrs['maxItems']),
    tagId: (attrs['tagId'] as string | null) ?? null,
    frameworkUid: (attrs['frameworkUid'] as string | null) ?? null,
    minGroups: toCount(attrs['minGroups'], 1),
    minItemsPerGroup: toCount(attrs['minItemsPerGroup'], 1),
    maxGroups: toLimit(attrs['maxGroups']),
    domains: Array.isArray(attrs['domains'])
      ? (attrs['domains'] as Partial<FrameworkEvidenceDomain>[]).map((domain) =>
          frameworkDomain({ ...domain, minItems: toCount(domain.minItems, 1) }),
        )
      : [],
    allowLearnerDomains: attrs['allowLearnerDomains'] === true || attrs['allowLearnerDomains'] === 'true',
  };
}

function toCount(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(parsed) ? parsed : fallback;
}

function toLimit(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
